import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import {
  ActivatePluginParams,
  AddPageParams,
  AddPageResponse,
  AllMilestonesParams,
  AllMilestonesResponse,
  DeletePageParams,
  DeletePageResponse,
  PagesParams,
  PagesResponse,
  PluginActivationResponse,
  SiteParams,
  SiteResponse,
  SitesAcrossWorkspacesResponse,
} from 'types/api/site';
import { baseApi } from './baseApi';

export const siteApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    addPage: builder.mutation<AddPageResponse, AddPageParams>({
      query: (data) => ({
        body: data,
        method: 'POST',
        url: '/site/page',
      }),
      invalidatesTags: [{ type: 'Pages', id: 'LIST' }],
    }),
    deletePage: builder.mutation<DeletePageResponse, DeletePageParams>({
      query: ({ pageId }) => ({
        method: 'DELETE',
        url: `/site/pages/${pageId}`,
      }),
    }),
    getAllMilestones: builder.query<AllMilestonesResponse, AllMilestonesParams>(
      {
        query: ({ siteId }) => `/site/milestone/all/${siteId}`,
      }
    ),
    getPages: builder.query<PagesResponse, PagesParams>({
      query: ({ siteId, proxy_method = 'injectable_script' }) =>
        `/site/pages/${siteId}?proxy_method=${proxy_method}`,
      providesTags: [{ type: 'Pages', id: 'LIST' }],
    }),
    getSites: builder.query<SiteResponse, SiteParams>({
      queryFn: async ({ host, workspace_id, filter_by }, api, _, baseQuery) => {
        const url = `/site`;
        const queryParams = new URLSearchParams();
        if (host) {
          queryParams.append('host', host);
        }
        if (workspace_id) {
          queryParams.append('workspace_id', workspace_id);
        }
        if (filter_by) {
          queryParams.append('filter_by', filter_by);
        }

        const result = await baseQuery(`${url}?${queryParams.toString()}`);

        if (result.error) {
          return { error: result.error as FetchBaseQueryError };
        }
        const response = result.data as SiteResponse;
        if (response.status) {
          return { data: response };
        } else {
          return {
            error: {
              status: 404,
              data: { message: 'No sites found' },
            } as FetchBaseQueryError,
          };
        }
      },
      providesTags: [{ type: 'Site', id: 'LIST' }],
    }),
    getSitesAcrossWorkspaces: builder.query<
      SitesAcrossWorkspacesResponse,
      Omit<SiteParams, 'workspace_id'>
    >({
      query: ({ host }) => `/site/across-workspaces?host=${host}`,
      providesTags: [{ type: 'WorkspaceSite', id: 'LIST' }],
    }),
    activatePlugin: builder.mutation<
      PluginActivationResponse,
      ActivatePluginParams
    >({
      query: (body) => ({
        url: `/site/activate`,
        method: 'POST',
        body: body,
      }),
    }),
  }),
});

export const {
  useAddPageMutation,
  useDeletePageMutation,
  useActivatePluginMutation,
  useLazyGetAllMilestonesQuery,
  useLazyGetPagesQuery,
  useLazyGetSitesQuery,
  useGetAllMilestonesQuery,
  useGetPagesQuery,
  useGetSitesQuery,
  // Export the new hook
  useGetSitesAcrossWorkspacesQuery,
  useLazyGetSitesAcrossWorkspacesQuery,
} = siteApi;
